/**
 * MarkedroidHistoryPage — päeva/kuu tasandi tulud + päike per park,
 * andmeallikas Markedroidi cloud-platvorm (app.markedroid.com REST API).
 *
 * Tulu ajalugu stiilis: kuupäevade kaupa, klõpsuga lahti pargid; ülal kasumid;
 * 8 metricat (Aku kasutus, Aku laadimine, PV tootlus, Kodune tarbimine,
 * Eksport, Import, Flex üles, Flex alla) + smart-sale + battery arbitraaž.
 */

import { useState, useEffect, useMemo } from 'react';

const PERIODS = [
  { id: '7d',         label: '7 päeva' },
  { id: '30d',        label: '30 päeva' },
  { id: 'this_week',  label: 'See nädal' },
  { id: 'this_month', label: 'See kuu' },
  { id: 'this_year',  label: 'See aasta' },
  { id: 'last_week',  label: 'Eelmine nädal' },
  { id: 'last_month', label: 'Eelmine kuu' },
  { id: 'last_year',  label: 'Eelmine aasta' },
  { id: 'custom',     label: 'Vali' },
];

// 8 user-visible metric
const METRICS = [
  { key: 'aku_kasutus',       label: 'Aku kasutus',       color: '#7c3aed' },
  { key: 'aku_laadimine',     label: 'Aku laadimine',     color: '#0ea5e9' },
  { key: 'paneelide_tootlus', label: 'Paneelide tootlus', color: '#d97706' },
  { key: 'kodune_kasutus',    label: 'Kodune kasutus',    color: '#9333ea' },
  { key: 'eksport',           label: 'Eksport',           color: '#15803d' },
  { key: 'import',            label: 'Import',            color: '#dc2626' },
  { key: 'flex_yles',         label: 'Flex üles',         color: '#f59e0b' },
  { key: 'flex_alla',         label: 'Flex alla',         color: '#6b7280' },
  { key: 'uhisvalmidus',      label: 'Ühisvalmidus',      color: '#059669' },
];

function computeRange(period, customFrom, customTo) {
  const now = Date.now();
  const d = new Date();
  const startOfDay = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  if (period === 'custom') {
    if (customFrom && customTo) return { from: new Date(customFrom).getTime(), to: new Date(customTo).getTime() + 86400e3 - 1 };
    return null;
  }
  if (period === '7d')  return { from: startOfDay - 7 * 86400e3, to: now };
  if (period === '30d') return { from: startOfDay - 30 * 86400e3, to: now };
  if (period === 'this_week') { const dow = (d.getDay() + 6) % 7; return { from: startOfDay - dow * 86400e3, to: now }; }
  if (period === 'this_month') return { from: new Date(d.getFullYear(), d.getMonth(), 1).getTime(), to: now };
  if (period === 'this_year')  return { from: new Date(d.getFullYear(), 0, 1).getTime(), to: now };
  if (period === 'last_week') {
    const dow = (d.getDay() + 6) % 7;
    const thisWeekStart = startOfDay - dow * 86400e3;
    return { from: thisWeekStart - 7 * 86400e3, to: thisWeekStart - 1 };
  }
  if (period === 'last_month') {
    return { from: new Date(d.getFullYear(), d.getMonth() - 1, 1).getTime(), to: new Date(d.getFullYear(), d.getMonth(), 1).getTime() - 1 };
  }
  if (period === 'last_year') {
    return { from: new Date(d.getFullYear() - 1, 0, 1).getTime(), to: new Date(d.getFullYear(), 0, 1).getTime() - 1 };
  }
  return null;
}

function fmtEur(n) {
  if (n == null || isNaN(n)) return '—';
  const v = Number(n);
  return v < 0 ? `−${Math.abs(v).toFixed(2)} €` : `${v.toFixed(2)} €`;
}
function fmtEurSigned(n) {
  if (n == null || isNaN(n)) return '—';
  const v = Number(n);
  return v >= 0 ? `+${v.toFixed(2)} €` : `−${Math.abs(v).toFixed(2)} €`;
}
function fmtKwh(n) {
  if (n == null || isNaN(n)) return '—';
  const v = Number(n);
  if (Math.abs(v) >= 1000) return `${(v / 1000).toFixed(2)} MWh`;
  return `${v.toFixed(1)} kWh`;
}

export function MarkedroidHistoryPage() {
  const [period, setPeriod] = useState('this_month');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [data, setData] = useState(null);              // {days|months: [...]} — periood-sõltuv
  const [monthlyAll, setMonthlyAll] = useState(null);  // KÕIK kuud alates 2025-06 (kindel)
  const [periodTotals, setPeriodTotals] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [expandedKey, setExpandedKey] = useState(null);
  const [expandedMonthKey, setExpandedMonthKey] = useState(null);

  const range = useMemo(() => computeRange(period, customFrom, customTo), [period, customFrom, customTo]);

  // Otsus: kas päevade tabel (≤35 päeva) või kuude tabel (kauem)
  const isDaily = useMemo(() => {
    if (!range) return false;
    return (range.to - range.from) <= 36 * 86400e3;
  }, [range]);

  const load = () => {
    if (!range) return;
    setLoading(true); setErr(null); setData(null);

    // Üla-summary: kõik perioodid kasutavad /period endpoint-i
    const periodReq = fetch(`/api/markedroid/period?from=${range.from}&to=${range.to}`)
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)));

    let rowsReq;
    if (isDaily) {
      rowsReq = fetch(`/api/markedroid/daily?from=${range.from}&to=${range.to}`)
        .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)));
    } else {
      // Kuude tabel — pikemate periodite jaoks
      const fromYM = `${new Date(range.from).getFullYear()}-${String(new Date(range.from).getMonth() + 1).padStart(2, '0')}`;
      const toYM   = `${new Date(range.to).getFullYear()}-${String(new Date(range.to).getMonth() + 1).padStart(2, '0')}`;
      rowsReq = fetch(`/api/markedroid/monthly?from=${fromYM}&to=${toYM}`)
        .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)));
    }

    Promise.all([periodReq, rowsReq])
      .then(([p, m]) => { setPeriodTotals(p); setData(m); })
      .catch(e => setErr(e.message))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [period, customFrom, customTo]);

  // Alumine "kuude lõikes" tabel — fetch üks kord, alates juuni 2026
  // kuni jooksva kuu lõpuni. See ei sõltu perioodi-valikust.
  useEffect(() => {
    const d = new Date();
    const fromYM = '2026-06';
    const toYM = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    fetch(`/api/markedroid/monthly?from=${fromYM}&to=${toYM}`)
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then(setMonthlyAll)
      .catch(() => {});
  }, []);

  const total = periodTotals?.total || null;
  const rows = isDaily ? (data?.days || []) : (data?.months || []);
  // Sort: uusim üleval
  const sortedRows = useMemo(() => {
    if (isDaily) return [...rows].sort((a, b) => b.date.localeCompare(a.date));
    return [...rows].sort((a, b) => b.month.localeCompare(a.month));
  }, [rows, isDaily]);
  const sortedMonths = useMemo(() => {
    if (!monthlyAll?.months) return [];
    return [...monthlyAll.months].sort((a, b) => b.month.localeCompare(a.month));
  }, [monthlyAll]);

  return (
    <div style={{ padding: '20px 24px', maxWidth: 1480, margin: '0 auto' }}>
      <h1 style={{ fontSize: 22, fontWeight: 600, marginBottom: 4 }}>Tulud ja Päike (Markedroid)</h1>
      <p style={{ color: '#666', fontSize: 13, marginTop: 0, marginBottom: 16 }}>
        Andmed otse app.markedroid.com kontolt — aku, päike, eksport/import, flex.
        {isDaily ? ' Päevatasand.' : ' Kuutasand (periood > 35 päeva).'}
      </p>

      {/* Perioodi nupud */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {PERIODS.map(p => (
          <button key={p.id} onClick={() => setPeriod(p.id)} style={{
            padding: '6px 14px', borderRadius: 4, border: '1px solid #ddd',
            background: period === p.id ? '#11093E' : '#fff',
            color: period === p.id ? '#fff' : '#333',
            fontWeight: 500, cursor: 'pointer', fontSize: 13,
          }}>{p.label}</button>
        ))}
        {period === 'custom' && (
          <>
            <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} style={inpStyle} />
            <span style={{ alignSelf: 'center' }}>—</span>
            <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} style={inpStyle} />
          </>
        )}
      </div>

      {err && <div style={{ padding: 10, background: '#ffebee', color: '#c62828', borderRadius: 4, marginBottom: 12 }}>Viga: {err}</div>}
      {loading && <div style={{ padding: 10, color: '#666' }}>Laeb… (võib võtta 10-30 sek esimene kord)</div>}

      {/* TOP: Tulu ajalugu stiilis 3 kasti — Tulu, Kulu, Netokasum */}
      {total && (
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 18,
          background: '#F9FAF9', padding: 16, borderRadius: 8, border: '1px solid #e0e0e0',
        }}>
          <div>
            <div style={lbl}>Tulu kokku</div>
            <div style={{ ...val, color: '#15803d' }}>{fmtEurSigned(total.tulu)}</div>
            <div style={sub}>{rows.length} {isDaily ? 'päeva' : 'kuud'}</div>
          </div>
          <div>
            <div style={lbl}>Kulu kokku</div>
            <div style={{ ...val, color: '#dc2626' }}>{fmtEur(-total.kulu)}</div>
            <div style={sub}>Markedroidi all</div>
          </div>
          <div>
            <div style={lbl}>Netokasum</div>
            <div style={{ ...val, color: (total.netokasum >= 0) ? '#15803d' : '#dc2626' }}>{fmtEurSigned(total.netokasum)}</div>
            <div style={sub}>{fmtKwh(total.paneelide_tootlus_kwh)} päike · {fmtKwh(total.eksport_kwh)} eksport</div>
          </div>
        </div>
      )}

      {/* PERIOOD TABEL — kuupäev/kuu | tulu | kulu | netokasum | 8 metric | pargid */}
      <h2 style={{ fontSize: 14, fontWeight: 600, margin: '6px 0 8px', color: '#555' }}>
        {isDaily ? '📅 Päevade lõikes (valitud periood)' : '📆 Kuude lõikes (valitud periood)'}
      </h2>
      {sortedRows.length > 0 ? (
        <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 6, overflow: 'hidden', marginBottom: 24 }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead style={{ background: '#f5f5f5' }}>
                <tr>
                  <th style={{ ...th, width: 100 }}>{isDaily ? 'Kuupäev' : 'Kuu'}</th>
                  <th style={thR}>Tulu</th>
                  <th style={thR}>Kulu</th>
                  <th style={thR}>Netokasum</th>
                  {METRICS.map(M => (
                    <th key={M.key} style={{ ...thR, color: M.color }}>{M.label}</th>
                  ))}
                  <th style={{ ...th, textAlign: 'center', width: 80 }}>Pargid</th>
                </tr>
              </thead>
              <tbody>
                {sortedRows.map(r => {
                  const key = isDaily ? r.date : r.month;
                  return <Row key={key} row={r} isDaily={isDaily} expanded={expandedKey === key} onToggle={() => setExpandedKey(expandedKey === key ? null : key)} />;
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (!loading && !err && (
        <div style={{ padding: 24, textAlign: 'center', color: '#888', marginBottom: 24 }}>Valitud perioodis pole andmeid.</div>
      ))}

      {/* ALUMINE — kuude lõikes (kindel, alates 2025-06, sõltumatu valitud perioodist) */}
      {sortedMonths.length > 0 && (
        <>
          <h2 style={{ fontSize: 14, fontWeight: 600, margin: '16px 0 8px', color: '#555' }}>
            📊 Kuude lõikes
          </h2>
          <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 6, overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead style={{ background: '#f5f5f5' }}>
                  <tr>
                    <th style={{ ...th, width: 100 }}>Kuu</th>
                    <th style={thR}>Tulu</th>
                    <th style={thR}>Kulu</th>
                    <th style={thR}>Netokasum</th>
                    {METRICS.map(M => (
                      <th key={M.key} style={{ ...thR, color: M.color }}>{M.label}</th>
                    ))}
                    <th style={{ ...th, textAlign: 'center', width: 80 }}>Pargid</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedMonths.map(m => (
                    <Row key={m.month} row={m} isDaily={false} expanded={expandedMonthKey === m.month} onToggle={() => setExpandedMonthKey(expandedMonthKey === m.month ? null : m.month)} />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Row({ row, isDaily, expanded, onToggle }) {
  const t = row.total || {};
  const label = isDaily ? row.date : fmtMonthEt(row.month);
  return (
    <>
      <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
        <td style={{ ...td, fontWeight: 500, fontFamily: 'monospace' }}>{label}</td>
        <td style={{ ...tdR, color: '#15803d', fontWeight: 500 }}>{fmtEurSigned(t.tulu)}</td>
        <td style={{ ...tdR, color: '#dc2626', fontWeight: 500 }}>{fmtEur(-t.kulu)}</td>
        <td style={{ ...tdR, fontWeight: 700, color: t.netokasum >= 0 ? '#15803d' : '#dc2626' }}>{fmtEurSigned(t.netokasum)}</td>
        {METRICS.map(M => (
          <td key={M.key} style={tdR}>
            <div>{fmtEur(t[`${M.key}_eur`])}</div>
            <div style={{ fontSize: 10, color: '#888' }}>{fmtKwh(t[`${M.key}_kwh`])}</div>
          </td>
        ))}
        <td style={{ ...td, textAlign: 'center' }}>
          <button onClick={onToggle} style={{ background: 'none', border: '1px solid #ccc', borderRadius: 4, padding: '2px 10px', cursor: 'pointer', fontSize: 12 }}>
            {expanded ? '▾' : `▸ ${row.parks?.length || 0}`}
          </button>
        </td>
      </tr>
      {expanded && (row.parks || []).map(p => (
        <tr key={p.slot} style={{ background: '#fafafa', fontSize: 11 }}>
          <td style={{ padding: '6px 12px 6px 32px', color: '#666' }}>{p.slot}. {p.name || p.markedroid_name}</td>
          <td style={{ ...tdR, color: '#15803d' }}>{fmtEurSigned(p.tulu)}</td>
          <td style={{ ...tdR, color: '#dc2626' }}>{fmtEur(-p.kulu)}</td>
          <td style={{ ...tdR, fontWeight: 600, color: p.netokasum >= 0 ? '#15803d' : '#dc2626' }}>{fmtEurSigned(p.netokasum)}</td>
          {METRICS.map(M => (
            <td key={M.key} style={tdR}>
              <div>{fmtEur(p[`${M.key}_eur`])}</div>
              <div style={{ fontSize: 10, color: '#888' }}>{fmtKwh(p[`${M.key}_kwh`])}</div>
            </td>
          ))}
          <td></td>
        </tr>
      ))}
    </>
  );
}

function fmtMonthEt(ym) {
  const names = ['Jaan','Veebr','Märts','Aprill','Mai','Juuni','Juuli','Aug','Sept','Okt','Nov','Dets'];
  const [y, m] = ym.split('-').map(Number);
  return `${names[m - 1]} ${y}`;
}

const th = { padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #ccc', fontSize: 10.5, fontWeight: 600 };
const thR = { ...th, textAlign: 'right' };
const td = { padding: '6px 10px', borderBottom: '1px solid #f0f0f0' };
const tdR = { ...td, textAlign: 'right' };
const inpStyle = { padding: '6px 10px', borderRadius: 4, border: '1px solid #ddd', fontSize: 13 };
const lbl = { fontSize: 12, color: '#8C8C8C', textTransform: 'uppercase', letterSpacing: '0.05em' };
const val = { fontSize: 24, fontWeight: 700 };
const sub = { fontSize: 11, color: '#8C8C8C', marginTop: 2 };

export default MarkedroidHistoryPage;
